import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Check, ChevronLeft, ChevronRight, Upload, X, Loader2, Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { CONDITIONS, UNITS, PRICE_TYPES, DELIVERY_OPTIONS, HARVEST_STATUS, CITIES } from "@/lib/constants";
import { LocationSelect } from "@/components/LocationSelect";
import { priceLabel } from "@/lib/format";
import api, { imageUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = ["Kategori", "Ürün Bilgisi", "Durum", "Fiyat", "Miktar", "Konum", "Fotoğraflar", "Teslimat", "Önizleme"];

const EMPTY = {
  category: "", subcategory: "", title: "", description: "", condition: "",
  price_type: "fixed", price: "", price_min: "", price_max: "", tiers: [],
  unit: "adet", quantity: "", min_order: "", city: "", district: "",
  delivery_options: [], pickup_available: false, images: [],
  is_wholesale: false, is_agriculture: false, harvest_status: "", estimated_quantity: "",
};

export default function ListingWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data.items)); }, []);
  useEffect(() => {
    if (isEdit) {
      api.get(`/listings/${id}`).then(({ data }) => {
        const l = data.listing;
        setF({
          ...EMPTY, ...l,
          price: l.price ?? "", price_min: l.price_min ?? "", price_max: l.price_max ?? "",
          quantity: l.quantity ?? "", min_order: l.min_order ?? "", estimated_quantity: l.estimated_quantity ?? "",
          tiers: l.tiers || [], subcategory: l.subcategory || "", district: l.district || "",
          harvest_status: l.harvest_status || "", delivery_options: l.delivery_options || [],
        });
        setLoaded(true);
      });
    }
  }, [id, isEdit]);

  const selectedCat = categories.find((c) => c.slug === f.category);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("images", [...f.images, ...urls]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Yükleme başarısız"); }
    finally { setUploading(false); }
  };

  const canNext = () => {
    switch (step) {
      case 0: return !!f.category;
      case 1: return f.title.trim().length >= 3;
      case 2: return !!f.condition;
      case 3:
        if (f.price_type === "fixed" || f.price_type === "negotiable") return !!f.price;
        if (f.price_type === "range") return !!f.price_min && !!f.price_max;
        if (f.price_type === "tiered") return f.tiers.length > 0;
        return true;
      case 5: return !!f.city;
      default: return true;
    }
  };

  const buildPayload = (asDraft) => ({
    category: f.category, subcategory: f.subcategory || null, title: f.title, description: f.description,
    condition: f.condition, price_type: f.price_type,
    price: f.price ? parseFloat(f.price) : null,
    price_min: f.price_min ? parseFloat(f.price_min) : null,
    price_max: f.price_max ? parseFloat(f.price_max) : null,
    tiers: f.price_type === "tiered" ? f.tiers.map((t) => ({ min_qty: parseInt(t.min_qty), max_qty: t.max_qty ? parseInt(t.max_qty) : null, price: parseFloat(t.price) })) : null,
    unit: f.unit, quantity: f.quantity ? parseFloat(f.quantity) : null, min_order: f.min_order ? parseFloat(f.min_order) : null,
    city: f.city, district: f.district || null, delivery_options: f.delivery_options,
    pickup_available: f.pickup_available, images: f.images,
    is_wholesale: f.is_wholesale, is_agriculture: f.is_agriculture,
    harvest_status: f.is_agriculture ? (f.harvest_status || null) : null,
    estimated_quantity: f.estimated_quantity ? parseFloat(f.estimated_quantity) : null,
    save_as_draft: asDraft,
  });

  const submit = async (asDraft = false) => {
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/listings/${id}`, buildPayload(asDraft));
        toast.success("İlan güncellendi");
      } else {
        await api.post("/listings", buildPayload(asDraft));
        toast.success(asDraft ? "Taslak kaydedildi" : "İlanınız yayına gönderildi (onay bekliyor)");
      }
      navigate("/panel/ilanlarim");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (!loaded) return <div className="py-20 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-2xl font-extrabold text-slate-900">{isEdit ? "İlanı Düzenle" : "Yeni İlan Oluştur"}</h1>

      {/* Stepper */}
      <div className="mt-5 flex items-center gap-1 overflow-x-auto no-scrollbar pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex shrink-0 items-center gap-1">
            <button onClick={() => i < step && setStep(i)} className="flex items-center gap-1.5">
              <span className={cn("flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold",
                i < step ? "bg-emerald-600 text-white" : i === step ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-600" : "bg-slate-100 text-slate-400")}>
                {i < step ? <Check size={14} /> : i + 1}
              </span>
              <span className={cn("text-xs font-medium", i === step ? "text-emerald-700" : "text-slate-400")}>{s}</span>
            </button>
            {i < STEPS.length - 1 && <span className="mx-1 h-px w-4 bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="mt-6 min-h-[320px] rounded-xl border border-slate-200 bg-white p-6">
        {/* STEP 0 Category */}
        {step === 0 && (
          <div>
            <StepTitle>Kategori Seçin</StepTitle>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {categories.map((c) => (
                <button key={c.slug} onClick={() => { set("category", c.slug); set("subcategory", ""); set("is_agriculture", c.slug === "tarlada-kalan"); set("is_wholesale", ["toptan", "palet-lot"].includes(c.slug)); }}
                  data-testid={`wiz-cat-${c.slug}`}
                  className={cn("rounded-lg border p-3 text-left text-sm font-medium transition-all",
                    f.category === c.slug ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                  {c.name}
                </button>
              ))}
            </div>
            {selectedCat?.subcategories?.length > 0 && (
              <div className="mt-4">
                <Label className="mb-2 block">Alt Kategori</Label>
                <Select value={f.subcategory} onValueChange={(v) => set("subcategory", v)}>
                  <SelectTrigger data-testid="wiz-subcat"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent>{selectedCat.subcategories.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* STEP 1 Product info */}
        {step === 1 && (
          <div className="space-y-4">
            <StepTitle>Ürün Bilgileri</StepTitle>
            <div className="space-y-1.5"><Label>Başlık *</Label>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="wiz-title" placeholder="Örn: 500 Adet Stok Fazlası Seramik" /></div>
            <div className="space-y-1.5"><Label>Açıklama</Label>
              <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={5} data-testid="wiz-desc" placeholder="Ürünün durumu, özellikleri ve satış koşulları..." /></div>
          </div>
        )}

        {/* STEP 2 Condition */}
        {step === 2 && (
          <div>
            <StepTitle>Ürün Durumu</StepTitle>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {CONDITIONS.map((c) => (
                <button key={c.value} onClick={() => set("condition", c.value)} data-testid={`wiz-cond-${c.value}`}
                  className={cn("flex flex-col items-center gap-2 rounded-lg border p-3 transition-all",
                    f.condition === c.value ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300")}>
                  <StatusBadge condition={c.value} />
                </button>
              ))}
            </div>
            {f.category === "tarlada-kalan" && (
              <div className="mt-5 space-y-1.5">
                <Label>Hasat Durumu</Label>
                <Select value={f.harvest_status} onValueChange={(v) => set("harvest_status", v)}>
                  <SelectTrigger data-testid="wiz-harvest"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                  <SelectContent>{HARVEST_STATUS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 Price */}
        {step === 3 && (
          <div className="space-y-4">
            <StepTitle>Fiyatlandırma</StepTitle>
            <div className="space-y-1.5"><Label>Fiyat Tipi</Label>
              <Select value={f.price_type} onValueChange={(v) => set("price_type", v)}>
                <SelectTrigger data-testid="wiz-price-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select></div>

            {(f.price_type === "fixed" || f.price_type === "negotiable") && (
              <div className="space-y-1.5"><Label>Fiyat (₺)</Label>
                <Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="wiz-price" /></div>
            )}
            {f.price_type === "range" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Min Fiyat (₺)</Label><Input type="number" value={f.price_min} onChange={(e) => set("price_min", e.target.value)} data-testid="wiz-price-min" /></div>
                <div className="space-y-1.5"><Label>Max Fiyat (₺)</Label><Input type="number" value={f.price_max} onChange={(e) => set("price_max", e.target.value)} data-testid="wiz-price-max" /></div>
              </div>
            )}
            {f.price_type === "tiered" && (
              <div className="space-y-2">
                <Label>Kademeli Fiyatlar</Label>
                {f.tiers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="number" placeholder="Min adet" value={t.min_qty} onChange={(e) => { const n = [...f.tiers]; n[i].min_qty = e.target.value; set("tiers", n); }} className="h-9" />
                    <Input type="number" placeholder="Max adet" value={t.max_qty || ""} onChange={(e) => { const n = [...f.tiers]; n[i].max_qty = e.target.value; set("tiers", n); }} className="h-9" />
                    <Input type="number" placeholder="Fiyat ₺" value={t.price} onChange={(e) => { const n = [...f.tiers]; n[i].price = e.target.value; set("tiers", n); }} className="h-9" />
                    <Button size="icon" variant="ghost" onClick={() => set("tiers", f.tiers.filter((_, x) => x !== i))}><X size={16} /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => set("tiers", [...f.tiers, { min_qty: "", max_qty: "", price: "" }])} data-testid="wiz-add-tier">+ Kademe Ekle</Button>
              </div>
            )}
            {f.price_type === "offer" && <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Alıcılar bu ilan için teklif verecek.</p>}
          </div>
        )}

        {/* STEP 4 Quantity */}
        {step === 4 && (
          <div className="space-y-4">
            <StepTitle>Miktar ve Birim</StepTitle>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Birim</Label>
                <Select value={f.unit} onValueChange={(v) => set("unit", v)}>
                  <SelectTrigger data-testid="wiz-unit"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Mevcut Miktar</Label><Input type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="wiz-quantity" /></div>
            </div>
            <div className="space-y-1.5"><Label>Minimum Sipariş</Label><Input type="number" value={f.min_order} onChange={(e) => set("min_order", e.target.value)} data-testid="wiz-min-order" /></div>
            {f.is_agriculture && (
              <div className="space-y-1.5"><Label>Tahmini Miktar ({f.unit})</Label><Input type="number" value={f.estimated_quantity} onChange={(e) => set("estimated_quantity", e.target.value)} data-testid="wiz-est-qty" /></div>
            )}
          </div>
        )}

        {/* STEP 5 Location */}
        {step === 5 && (
          <div className="space-y-4">
            <StepTitle>Konum</StepTitle>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LocationSelect city={f.city} district={f.district} required
                onChange={(city, district) => setF((prev) => ({ ...prev, city, district }))}
                cityTestId="wiz-city" districtTestId="wiz-district" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><p className="text-sm font-medium text-slate-800">Toptan / Lot ilanı</p><p className="text-xs text-slate-500">Toptan Fırsatlar bölümünde listelensin</p></div>
              <Switch checked={f.is_wholesale} onCheckedChange={(v) => set("is_wholesale", v)} data-testid="wiz-wholesale" />
            </div>
          </div>
        )}

        {/* STEP 6 Photos */}
        {step === 6 && (
          <div>
            <StepTitle>Fotoğraflar</StepTitle>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {f.images.map((img, i) => (
                <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200">
                  <img src={imageUrl(img)} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => set("images", f.images.filter((_, x) => x !== i))} data-testid={`wiz-remove-img-${i}`}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"><X size={14} /></button>
                </div>
              ))}
              <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-500" data-testid="wiz-upload">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
                <span className="text-xs">Yükle</span>
                <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
                  onChange={(e) => { if (e.target.files.length) uploadFiles(Array.from(e.target.files)); e.target.value = ""; }} />
              </label>
            </div>
            <p className="mt-3 text-xs text-slate-500">JPG, PNG veya WEBP. Dosya başına en fazla 8MB.</p>
          </div>
        )}

        {/* STEP 7 Delivery */}
        {step === 7 && (
          <div className="space-y-4">
            <StepTitle>Teslimat Seçenekleri</StepTitle>
            <div className="space-y-2">
              {DELIVERY_OPTIONS.map((d) => (
                <label key={d} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm">
                  <Checkbox checked={f.delivery_options.includes(d)}
                    onCheckedChange={(ch) => set("delivery_options", ch ? [...f.delivery_options, d] : f.delivery_options.filter((x) => x !== d))}
                    data-testid={`wiz-delivery-${d}`} />
                  {d}
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-800">Yerinden teslim alınabilir</p>
              <Switch checked={f.pickup_available} onCheckedChange={(v) => set("pickup_available", v)} data-testid="wiz-pickup" />
            </div>
          </div>
        )}

        {/* STEP 8 Preview */}
        {step === 8 && (
          <div>
            <StepTitle>Önizleme</StepTitle>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="aspect-[16/9] bg-slate-100">
                {f.images[0] ? <img src={imageUrl(f.images[0])} alt="" className="h-full w-full object-cover" /> :
                  <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon /></div>}
              </div>
              <div className="space-y-2 p-4">
                {f.condition && <StatusBadge condition={f.condition} />}
                <h3 className="font-display text-lg font-bold text-slate-900">{f.title || "Başlık"}</h3>
                <p className="text-xl font-extrabold text-emerald-900">{priceLabel({ ...f, price: parseFloat(f.price) || null, price_min: parseFloat(f.price_min) || null, price_max: parseFloat(f.price_max) || null, tiers: f.tiers.map((t) => ({ ...t, price: parseFloat(t.price) })) })}</p>
                <p className="text-sm text-slate-600">{f.city}{f.district ? ` / ${f.district}` : ""}</p>
                <p className="text-sm text-slate-600">{f.description}</p>
              </div>
            </div>
            <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">İlanınız yayına gönderildikten sonra yönetici onayından geçecektir.</p>
          </div>
        )}
      </div>

      {/* Nav buttons */}
      <div className="mt-5 flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wiz-prev" className="gap-1"><ChevronLeft size={16} /> Geri</Button>
        <div className="flex gap-2">
          {step === STEPS.length - 1 ? (
            <>
              <Button variant="outline" onClick={() => submit(true)} disabled={submitting} data-testid="wiz-draft">Taslak Kaydet</Button>
              <Button onClick={() => submit(false)} disabled={submitting} data-testid="wiz-publish" className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Güncelle" : "Yayınla"}
              </Button>
            </>
          ) : (
            <Button onClick={() => { if (canNext()) setStep((s) => s + 1); else toast.error("Lütfen gerekli alanları doldurun"); }}
              data-testid="wiz-next" className="gap-1 bg-emerald-600 hover:bg-emerald-700">İleri <ChevronRight size={16} /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepTitle({ children }) {
  return <h2 className="mb-4 font-display text-lg font-bold text-slate-900">{children}</h2>;
}
